import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { Search, ShieldCheck } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import { EmptyState, PageHeader } from '../components/app/index.ts';
import { moduleAPI } from '../services/api.ts';
import { toast } from '../lib/toast.ts';

const isReport = (module: any) =>
  String(module.module_name || '')
    .toLowerCase()
    .includes('report');

// Reports grouped as the team dashboard lists them, each group in its own
// order. A report not named here goes under "Other reports".
const REPORT_GROUPS: { title: string; codes: string[] }[] = [
  {
    title: 'Accounting',
    codes: [
      'account_status_region_report',
      'account_status_territory_report',
      'account_status_area_report',
      'account_status_policy_report',
      'bm_account_status_area_wise_report',
      'bm_account_status_policy_wise_report',
      'credit_limit_report',
      'tcl_status_report',
    ],
  },
  {
    title: 'Collection',
    codes: [
      'collection_region_report',
      'collection_territory_report',
      'collection_detail_region_report',
      'collection_detail_territory_report',
      'collection_summary_report',
      'collection_commission_report',
    ],
  },
  {
    title: 'Sale',
    codes: [
      'product_sale_summary_report',
      'product_ledger_report',
      'bm_product_sale_report',
      'bm_product_ledger_summary_report',
      'territory_product_sale_report',
      'category_wise_sale_analysis_report',
      'sale_analysis_report',
      'fpl_sale_report',
    ],
  },
  {
    title: 'Target Vs Achievement',
    codes: [
      'target_vs_achievement_report',
      'target_vs_achievement_brand_report',
      'target_vs_achievement_category_report',
    ],
  },
  {
    title: 'NAP',
    codes: [
      'rsm_nap_achievement_report',
      'rsm_nap_target_vs_achievement_report',
      'tm_nap_achievement_report',
      'tm_nap_target_vs_achievement_report',
    ],
  },
];

const KNOWN_REPORTS = REPORT_GROUPS.flatMap((group) => group.codes);

/** The reports of one group, in the group's order. */
const inGroup = (modules: any[], codes: string[]) =>
  codes
    .map((code) => modules.find((module) => module.module_code === code))
    .filter(Boolean);

const isGranted = (module: any) =>
  (module.granted_permissions || []).length > 0;

const label = (permission: string) =>
  permission.charAt(0).toUpperCase() + permission.slice(1);

// The granted permissions as one comparable string, to tell what changed.
const signature = (modules: any[]) =>
  modules
    .map(
      (module) =>
        `${module.module_id}:${[...(module.granted_permissions || [])].sort().join(',')}`
    )
    .join('|');

/** The permissions screen for one user: `/employees/:id/permissions`. */
export const EmployeePermissions: React.FC = () => {
  const { id } = useParams();
  const employeeId = Number(id);
  const navigate = useNavigate();
  const back = `/employees/${id}`;

  const [modules, setModules] = useState<any[]>([]);
  const [saved, setSaved] = useState('');
  const [employee, setEmployee] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    moduleAPI
      .getEmployeePermissions(employeeId)
      .then((response) => {
        if (cancelled) return;
        if (!response.success) {
          throw new Error(response.message || 'Failed to load permissions');
        }
        setModules(response.data.permissions || []);
        setSaved(signature(response.data.permissions || []));
        setEmployee(response.data.employee);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              err.message ||
              'Failed to load permissions'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  const changed = signature(modules) !== saved;

  const toggle = (moduleId: number, permission: string) =>
    setModules((current) =>
      current.map((module) => {
        if (module.module_id !== moduleId) return module;
        const granted: string[] = module.granted_permissions || [];
        return {
          ...module,
          granted_permissions: granted.includes(permission)
            ? granted.filter((p) => p !== permission)
            : [...granted, permission],
        };
      })
    );

  // Grants or removes everything in one section (modules or reports), or
  // in one group of reports when `codes` is given.
  const setAll = (reports: boolean, grant: boolean, codes?: string[]) =>
    setModules((current) =>
      current.map((module) =>
        isReport(module) === reports &&
        (!codes || codes.includes(module.module_code))
          ? {
              ...module,
              granted_permissions: grant
                ? [...(module.available_permissions || [])]
                : [],
            }
          : module
      )
    );

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      await moduleAPI.updateEmployeePermissions(employeeId, modules);
      toast.success('Permissions updated successfully');
      navigate(back);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to update permissions'
      );
    } finally {
      setSaving(false);
    }
  };

  const term = search.trim().toLowerCase();
  const shown = useMemo(
    () =>
      term
        ? modules.filter((module) =>
            String(module.module_name || '')
              .toLowerCase()
              .includes(term)
          )
        : modules,
    [modules, term]
  );

  const grid = (items: any[]) => (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      {items.map((module) => {
        const granted: string[] = module.granted_permissions || [];
        return (
          <Paper
            key={module.module_id}
            withBorder
            px="md"
            py="sm"
            bg={
              granted.length > 0
                ? 'var(--mantine-primary-color-light)'
                : undefined
            }
          >
            <Group justify="space-between" wrap="nowrap" gap="sm">
              <Text size="sm" fw={500}>
                {module.module_name}
              </Text>
              <Group gap="md" wrap="nowrap">
                {(module.available_permissions || []).map(
                  (permission: string) => (
                    <Switch
                      key={permission}
                      label={label(permission)}
                      labelPosition="left"
                      checked={granted.includes(permission)}
                      onChange={() => toggle(module.module_id, permission)}
                    />
                  )
                )}
              </Group>
            </Group>
          </Paper>
        );
      })}
    </SimpleGrid>
  );

  // The reports shown, by group, each with its own count and allow switch.
  const groups = (list: any[]) => {
    const other = list
      .filter((module) => !KNOWN_REPORTS.includes(module.module_code))
      .sort((a, b) =>
        String(a.module_name).localeCompare(String(b.module_name))
      );
    return [
      ...REPORT_GROUPS.map((group) => ({
        title: group.title,
        items: inGroup(list, group.codes),
      })),
      { title: 'Other reports', items: other },
    ]
      .filter((group) => group.items.length > 0)
      .map((group) => {
        const codes = group.items.map((module) => module.module_code);
        const on = group.items.filter(isGranted).length;
        const allOn = on === group.items.length;
        return (
          <Stack key={group.title} gap="xs">
            <Group justify="space-between" gap="sm">
              <Group gap="xs">
                <Text size="sm" fw={600} c="dimmed" tt="uppercase">
                  {group.title}
                </Text>
                <Badge
                  size="sm"
                  variant="light"
                  color={on > 0 ? 'green' : 'gray'}
                >
                  {on} of {group.items.length}
                </Badge>
              </Group>
              <Button
                size="compact-xs"
                variant="subtle"
                color={allOn ? 'gray' : undefined}
                onClick={() => setAll(true, !allOn, codes)}
              >
                {allOn ? 'Remove all' : 'Allow all'}
              </Button>
            </Group>
            {grid(group.items)}
          </Stack>
        );
      });
  };

  const section = (title: string, description: string, reports: boolean) => {
    const all = modules.filter((module) => isReport(module) === reports);
    if (all.length === 0) return null;
    const list = shown.filter((module) => isReport(module) === reports);
    const on = all.filter(isGranted).length;

    return (
      <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" gap="sm">
            <div>
              <Group gap="xs">
                <Text size="lg" fw={600}>
                  {title}
                </Text>
                <Badge variant="light" color={on > 0 ? 'green' : 'gray'}>
                  {on} of {all.length} allowed
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {description}
              </Text>
            </div>
            <Group gap="xs">
              <Button
                size="compact-sm"
                variant="light"
                onClick={() => setAll(reports, true)}
              >
                Allow all
              </Button>
              <Button
                size="compact-sm"
                variant="subtle"
                color="gray"
                onClick={() => setAll(reports, false)}
              >
                Remove all
              </Button>
            </Group>
          </Group>

          {list.length === 0 ? (
            <Text size="sm" c="dimmed">
              Nothing here matches "{search.trim()}".
            </Text>
          ) : reports ? (
            <Stack gap="lg">{groups(list)}</Stack>
          ) : (
            grid(list)
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <Layout>
      <Box maw={960} mx="auto">
        <PageHeader
          back={back}
          title="Permissions"
          description={
            employee?.name
              ? `What ${employee.name} can open in the app and the team dashboard.`
              : undefined
          }
          actions={
            employee?.is_region_manager ? (
              <Badge size="lg" variant="light" color="grape">
                Regional Manager
              </Badge>
            ) : employee?.is_territory_manager ? (
              <Badge size="lg" variant="light" color="blue">
                Territory Manager
              </Badge>
            ) : undefined
          }
        />

        {loading ? (
          <Group justify="center" h={240}>
            <Loader />
          </Group>
        ) : (
          <Stack gap="lg">
            {error && (
              <Alert color="red" variant="light">
                {error}
              </Alert>
            )}

            {modules.length === 0 ? (
              !error && (
                <Paper withBorder shadow="sm">
                  <EmptyState
                    icon={<ShieldCheck size={28} />}
                    title="No modules to allow"
                    description="There are no active modules yet."
                  />
                </Paper>
              )
            ) : (
              <>
                <TextInput
                  type="search"
                  placeholder="Find a module or report..."
                  leftSection={<Search size={16} />}
                  value={search}
                  onChange={(event) => setSearch(event.currentTarget.value)}
                />

                {section('Modules', 'The sections this user can open.', false)}
                {section('Reports', 'The reports this user can run.', true)}

                {/* Kept in view while the list scrolls. */}
                <Paper
                  withBorder
                  shadow="md"
                  p="md"
                  pos="sticky"
                  bottom={16}
                  style={{ zIndex: 5 }}
                >
                  <Group justify="space-between" gap="sm">
                    <Text size="sm" c="dimmed">
                      {changed
                        ? 'You have unsaved changes.'
                        : 'No changes yet.'}
                    </Text>
                    <Group gap="sm">
                      <Button variant="default" onClick={() => navigate(back)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={save}
                        loading={saving}
                        disabled={!changed}
                      >
                        Save changes
                      </Button>
                    </Group>
                  </Group>
                </Paper>
              </>
            )}
          </Stack>
        )}
      </Box>
    </Layout>
  );
};

export default EmployeePermissions;
